import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { useConvexAuth } from "convex/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useNetworkStatus } from "@/hooks";
import { getWorkOSConvexTokenStatusSnapshot } from "@/hooks/use-auth-from-workos";
import { breadcrumb } from "@/lib/observability";
import {
  canAttemptReloadRecovery,
  clearRememberedAuthRedirect,
  reloadDocument,
  rememberAuthRedirect,
  rememberReloadRecovery,
  resolveAuthRedirectDestination,
  toAppPath,
} from "./-redirect-storage";

export const Route = createFileRoute("/auth/login")({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: toAppPath(search.redirect),
  }),
});

/**
 * If a sign-in/sign-out/reload navigation started but this page is still
 * mounted after this long, the browser navigation silently failed (network
 * dropped mid-wake, WorkOS unreachable). Surface a manual retry instead of
 * spinning forever.
 */
const REDIRECT_WATCHDOG_MS = 10_000;

/**
 * How long a stale session waits for Convex to reconnect and finish validating
 * before the page stops trusting the in-memory client stack. After
 * wake-from-sleep the Convex client can wedge with its socket stopped and
 * nothing in-app restarts it (LAT-902); a document reload rebuilds it from the
 * still-valid WorkOS refresh token, which is exactly what a manual refresh did.
 */
const CONVEX_SETTLE_TIMEOUT_MS = 10_000;

/** A full-page navigation this page has started and is waiting to take effect. */
type PendingNavigation = "sign-in" | "sign-out" | "reload";

/** Why the page gave up and is showing the manual "Try again" screen. */
type StallReason = "sign-in-service" | "server-unreachable" | "reload-failed";

/**
 * Login page that automatically redirects to WorkOS AuthKit.
 *
 * Instead of showing a custom login screen, this component immediately
 * triggers the signIn flow which redirects to WorkOS for authentication.
 * This provides a seamless experience where users go directly to the
 * enterprise login without an intermediate "click to sign in" step.
 *
 * The redirect parameter is preserved through the auth flow via the
 * `state.returnTo` option, which WorkOS passes back to our callback.
 *
 * Recovery ladder for a *stale* session (AuthKit still has a user but Convex
 * does not report it as authenticated — typical after wake-from-sleep):
 *
 * 1. Wait, bounded by `CONVEX_SETTLE_TIMEOUT_MS`, for Convex to reconnect and
 *    settle: "not authenticated" while the socket is down may just mean "not
 *    confirmed yet", and acting on it would discard a live session (LAT-840).
 * 2. Reload the document into the original destination. This rebuilds AuthKit
 *    and the Convex client from the persisted WorkOS refresh token, which
 *    recovers both a wedged client and a transient token-refresh failure
 *    without forcing the user to log in again (LAT-902). Allowed once per
 *    `RELOAD_RECOVERY_COOLDOWN_MS` per tab.
 * 3. If a recent reload did not fix it, the session is genuinely rejected:
 *    sign out through WorkOS so the user gets a clean sign-in.
 *
 * Every step ends in a full browser navigation that fails silently when the
 * network is down, so each attempt only fires while the browser is online,
 * re-arms when connectivity returns, and falls back to a manual retry button
 * when a started navigation never completes.
 */
function LoginPage() {
  const { signIn, signOut, isLoading: authKitLoading, user } = useAuth();
  const { isAuthenticated, isLoading: convexLoading } = useConvexAuth();
  const { status: networkStatus, isOnline, isConvexConnected } = useNetworkStatus();
  const navigate = useNavigate();
  const { redirect: redirectTo } = Route.useSearch();

  // Guards a single navigation attempt; reset when the attempt is known dead
  // (offline transition, watchdog timeout, thrown error) so it can re-fire.
  const navigationStartedRef = useRef(false);
  // Bumped whenever the current attempt is invalidated, so a late rejection
  // from a superseded signIn() promise can't clobber the live attempt's state.
  const attemptGenerationRef = useRef(0);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const [stall, setStall] = useState<StallReason | null>(null);
  const [convexSettleTimedOut, setConvexSettleTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const convexSettled = !convexLoading && isConvexConnected;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user && !authKitLoading) {
      const destination = resolveAuthRedirectDestination(redirectTo);
      clearRememberedAuthRedirect();
      // `href`, not `to`: only `href` is parsed for `?query` / `#hash`.
      navigate({ href: destination });
    }
  }, [isAuthenticated, user, authKitLoading, navigate, redirectTo]);

  const markRedirectFailed = useCallback(
    (generation: number, operation: "sign-in" | "sign-out", error: unknown) => {
      if (generation !== attemptGenerationRef.current) {
        return;
      }
      breadcrumb(
        `auth.login.${operation}-failed`,
        { message: error instanceof Error ? error.message : String(error) },
        { category: "user", level: "warning" }
      );
      navigationStartedRef.current = false;
      setPendingNavigation(null);
      setStall("sign-in-service");
    },
    []
  );

  // A pending navigation cannot complete once the browser drops offline.
  // Clearing the guard lets the auto-trigger fire again when connectivity
  // returns, instead of leaving a spinner that nothing can ever resolve.
  useEffect(() => {
    if (!isOnline) {
      attemptGenerationRef.current += 1;
      navigationStartedRef.current = false;
      setPendingNavigation(null);
      setStall(null);
      setConvexSettleTimedOut(false);
    }
  }, [isOnline]);

  // Bounded wait for Convex to settle a stale session. Any moment Convex does
  // settle, the auto-trigger below acts immediately, so a plain timer keyed on
  // the wait condition is enough: a bouncing socket cannot postpone recovery.
  const waitingForConvex =
    Boolean(user) &&
    !isAuthenticated &&
    !authKitLoading &&
    isOnline &&
    !stall &&
    !pendingNavigation &&
    !convexSettled;
  useEffect(() => {
    if (!waitingForConvex) {
      return;
    }
    const timeoutId = setTimeout(() => setConvexSettleTimedOut(true), CONVEX_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [waitingForConvex]);

  // Auto-trigger the next step of the recovery ladder: reload/sign-out for
  // stale sessions (AuthKit has a user Convex rejects), sign-in when there is
  // no session at all.
  useEffect(() => {
    if (navigationStartedRef.current || stall) {
      return;
    }
    if (authKitLoading || isAuthenticated || !isOnline) {
      return;
    }

    const generation = attemptGenerationRef.current;
    const destination = redirectTo || "/";

    if (user) {
      if (!convexSettled && !convexSettleTimedOut) {
        return;
      }

      const tokenStatus = getWorkOSConvexTokenStatusSnapshot();
      const detail = {
        attempt,
        convexLoading,
        isConvexConnected,
        tokenStatus: tokenStatus.status,
        tokenReason: tokenStatus.reason ?? null,
      };

      // The cooldown marker must be persisted *before* reloading: if it cannot
      // be, the next document would reload again, so skip the automatic reload
      // and fall through to the next rung.
      if (canAttemptReloadRecovery() && rememberReloadRecovery()) {
        navigationStartedRef.current = true;
        setPendingNavigation("reload");
        breadcrumb(
          "auth.login.reload-recovery",
          { ...detail, reason: convexSettled ? "session-rejected" : "convex-unsettled" },
          { category: "user", level: "warning" }
        );
        reloadDocument(destination);
        return;
      }

      if (!convexSettled) {
        // A reload already ran recently and Convex is still unreachable:
        // signing out cannot help, so hand control to the user.
        breadcrumb("auth.login.convex-unreachable", detail, {
          category: "user",
          level: "warning",
        });
        setStall("server-unreachable");
        return;
      }

      // Convex is reachable, settled, and still rejects the session even after
      // a fresh client stack: the session itself is stale. Sign out so WorkOS
      // starts a clean sign-in.
      navigationStartedRef.current = true;
      setPendingNavigation("sign-out");
      rememberAuthRedirect(destination);
      breadcrumb("auth.login.sign-out-stale-session", detail, {
        category: "user",
        level: "warning",
      });
      try {
        signOut({
          returnTo: `${window.location.origin}/auth/login?redirect=${encodeURIComponent(
            destination
          )}`,
        });
      } catch (error) {
        markRedirectFailed(generation, "sign-out", error);
      }
      return;
    }

    navigationStartedRef.current = true;
    setPendingNavigation("sign-in");
    rememberAuthRedirect(destination);
    breadcrumb("auth.login.sign-in", { attempt }, { category: "user" });
    signIn({ state: { returnTo: destination } }).catch((error) => {
      markRedirectFailed(generation, "sign-in", error);
    });
  }, [
    attempt,
    authKitLoading,
    convexLoading,
    convexSettled,
    convexSettleTimedOut,
    isAuthenticated,
    isConvexConnected,
    isOnline,
    markRedirectFailed,
    redirectTo,
    signIn,
    signOut,
    stall,
    user,
  ]);

  // Watchdog: every step is a full browser navigation, so if this page is
  // still mounted after the timeout the navigation failed without an error.
  useEffect(() => {
    if (!pendingNavigation) {
      return;
    }
    const timeoutId = setTimeout(() => {
      breadcrumb(
        "auth.login.redirect-stalled",
        { attempt, navigation: pendingNavigation },
        { category: "user", level: "warning" }
      );
      attemptGenerationRef.current += 1;
      navigationStartedRef.current = false;
      setPendingNavigation(null);
      setStall(pendingNavigation === "reload" ? "reload-failed" : "sign-in-service");
    }, REDIRECT_WATCHDOG_MS);
    return () => clearTimeout(timeoutId);
  }, [pendingNavigation, attempt]);

  // The "server unreachable" stall is not terminal: if Convex does come back
  // and settles, resume the ladder (it will reload or sign out as appropriate).
  useEffect(() => {
    if (stall === "server-unreachable" && convexSettled) {
      setStall(null);
    }
  }, [stall, convexSettled]);

  const retry = () => {
    attemptGenerationRef.current += 1;
    if (stall === "server-unreachable" || stall === "reload-failed") {
      // An explicit user action: reload regardless of the automatic cooldown,
      // but still stamp it so the bounce back cannot add an automatic reload.
      breadcrumb("auth.login.manual-reload", { attempt }, { category: "user" });
      rememberReloadRecovery();
      reloadDocument(redirectTo || "/");
      return;
    }
    setStall(null);
    setConvexSettleTimedOut(false);
    setAttempt((current) => current + 1);
  };

  // The auth layout handles centering, so we just render the content
  if (!isOnline) {
    return (
      <div className="text-center">
        <p className="text-foreground font-medium mb-2">You're offline</p>
        <p className="text-muted-foreground text-sm">
          Sign-in will continue automatically when your connection returns.
        </p>
      </div>
    );
  }

  if (stall) {
    return (
      <div className="text-center">
        <p className="text-foreground font-medium mb-2">
          {stall === "sign-in-service"
            ? "We couldn't reach the sign-in service"
            : "We couldn't reconnect to the server"}
        </p>
        <p className="text-muted-foreground text-sm mb-4">Check your connection, then try again.</p>
        <button
          type="button"
          onClick={retry}
          className="text-sm text-primary hover:underline font-medium"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!pendingNavigation && networkStatus === "reconnecting") {
    return (
      <div className="text-center">
        <Spinner className="size-8 text-primary mx-auto mb-4" />
        <p className="text-muted-foreground text-sm">
          Reconnecting to the server... Sign-in will continue once the connection is restored.
        </p>
      </div>
    );
  }

  return (
    <div className="text-center">
      <Spinner className="size-8 text-primary mx-auto mb-4" />
      <p className="text-muted-foreground text-sm">
        {pendingNavigation === "sign-in" || pendingNavigation === "sign-out"
          ? "Redirecting to sign in..."
          : user
            ? "Restoring your session..."
            : "Preparing sign in..."}
      </p>
    </div>
  );
}
